import { useState, useEffect } from 'react';
import { getMachineImagePath } from '../utils/machineImageUtils';
import { translateMachineType } from '../utils/sapMetadataTranslations';
import { styles } from '../styles';

interface MachinePictureProps {
  machineId?: string;
  machineType?: string;
}

const MachinePicture = ({ machineId, machineType }: MachinePictureProps) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = () => {
      setLoading(true);
      setImageError(false);
      setImageSrc(''); // Clear previous image
      
      // Use machineType (bautyp) to load image
      if (!machineType && !machineId) {
        setImageError(true);
        setLoading(false);
        return;
      }
      
      try {
        const typeToUse = machineType || machineId || '';
        const imagePath = getMachineImagePath(typeToUse);
        if (imagePath) {
          setImageSrc(imagePath);
        } else {
          setImageError(true);
        }
      } catch (error) {
        setImageError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [machineId, machineType]);

  if (loading) {
    return (
      <div className={styles.machinePicture.container}>
        <div className={styles.machinePicture.loading.container}>
          <div className={styles.machinePicture.loading.spinner}></div>
          <div className={styles.machinePicture.loading.text}>Loading image...</div>
        </div>
      </div>
    );
  }

  if (imageError || !imageSrc) {
    return (
      <div className={styles.machinePicture.container}>
        <div className={styles.machinePicture.error.container}>
          <div className={styles.machinePicture.error.title}>
            {machineId || 'Machine'}
          </div>
          {machineType && (
            <div className={styles.machinePicture.error.subtitle}>
              {translateMachineType(machineType)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.machinePicture.container}>
      <img 
        src={imageSrc}
        alt={`${machineId || 'Machine'} - ${translateMachineType(machineType || 'Unknown Type')}`}
        className={styles.machinePicture.image}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

export default MachinePicture;
